/**
 * ReviewsModal.tsx — TABLERO de reseñas de un negocio en un MODAL ANCHO.
 *
 * Por qué un modal ancho y no el drawer: el tablero de tarjetas con FOTOS
 * necesita ancho para respirar (el drawer son 432px). El ÁNGULO DE VENTA
 * resumido se queda en la ficha (drawer); aquí va el detalle: tarjetas de
 * reseña (autor + avatar, fecha/time_ago, estrellas individuales, texto,
 * miniaturas de fotos, respuesta del dueño, badge Local Guide), con vista
 * CUADRÍCULA / TABLA, filtro "solo con fotos", orden y paginación.
 *
 * Flujo async (poll-on-read, sin worker): al abrir, hace GET; si no hay job
 * lo dispara con POST y hace polling del GET (que AVANZA el job) hasta
 * `status==='completed'`. Estados: cargando (con contador), vacío, error.
 *
 * Verdad de contrato: backend rama feature/reviews-module
 * (controllers/reviews.controller.ts + services/reviews_service.ts).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { InboxItem, BusinessReview, ReviewJob, ReviewsSort } from '@/services/listingsService'
import { requestReviews, getReviews } from '@/services/listingsService'
import { DEMO_REVIEWS, DEMO_REVIEW_JOB } from '@/pages/dashboardDemo'

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 } as const

const IS_PREVIEW =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('preview')

const PAGE_SIZE = 24
const POLL_MS = 2500

const SORT_OPTIONS: Array<{ value: ReviewsSort; label: string }> = [
  { value: 'newest', label: 'Más recientes' },
  { value: 'oldest', label: 'Más antiguas' },
  { value: 'lowest_rating', label: 'Peor valoradas' },
  { value: 'highest_rating', label: 'Mejor valoradas' },
]

/** Estrellas (rellenas/vacías) de una reseña individual. */
function Stars({ rating }: { rating: number | null }) {
  const n = Math.round(rating ?? 0)
  return (
    <span className="rv-stars" aria-label={rating != null ? `${rating} de 5` : 'Sin nota'}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={`rv-star${i <= n ? ' on' : ''}`}>★</span>
      ))}
    </span>
  )
}

/** Fecha humana: prefiere `time_ago` del proveedor; si no, deriva del ISO. */
function reviewDate(r: BusinessReview): string {
  if (r.time_ago && r.time_ago.trim()) return r.time_ago
  if (!r.review_timestamp) return ''
  const d = new Date(r.review_timestamp)
  if (Number.isNaN(d.getTime())) return ''
  const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

/** Iniciales del autor para el avatar de fallback. */
function initials(name: string | null): string {
  const n = (name ?? '').trim()
  if (!n) return '?'
  const parts = n.split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?'
}

/** Avatar con onError elegante (cae a iniciales). */
function Avatar({ review }: { review: BusinessReview }) {
  const [broken, setBroken] = useState(false)
  const url = review.author_image_url
  if (url && !broken) {
    return <img className="rv-avatar" src={url} alt="" loading="lazy" onError={() => setBroken(true)} />
  }
  return <span className="rv-avatar rv-avatar-fb">{initials(review.author_name)}</span>
}

/** Miniatura de foto con onError (las URLs del CDN de Google caducan). */
function Thumb({ src, onOpen }: { src: string; onOpen: (src: string) => void }) {
  const [broken, setBroken] = useState(false)
  if (broken) return null
  return (
    <button type="button" className="rv-thumb" onClick={() => onOpen(src)} aria-label="Ver foto">
      <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} />
    </button>
  )
}

/** Escape CSV (misma convención que el export de leads, searchShared). */
function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return ''
  const s = String(v)
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

/** Fecha para export: ISO si la hay, si no el `time_ago` del proveedor. */
function exportDate(r: BusinessReview): string {
  if (r.review_timestamp) {
    const d = new Date(r.review_timestamp)
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  }
  return r.time_ago ?? ''
}

/** Convierte las reseñas a CSV (campos útiles para el vendedor). */
function reviewsToCsv(items: BusinessReview[]): string {
  const cols = ['autor', 'fecha', 'nota', 'texto', 'n_fotos', 'local_guide', 'respuesta_dueno'] as const
  const header = cols.join(',')
  const rows = items.map((r) =>
    [
      r.author_name ?? '',
      exportDate(r),
      r.rating ?? '',
      r.text ?? '',
      r.photos.length,
      r.local_guide ? 'sí' : 'no',
      r.owner_answer ?? '',
    ]
      .map(csvEscape)
      .join(','),
  )
  return [header, ...rows].join('\n')
}

/** Dispara la descarga de un Blob (mismo patrón que el export de leads). */
function triggerDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

/** Slug seguro para el nombre de fichero. */
function slugify(s: string): string {
  return (s || 'negocio')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'negocio'
}

/** Aplica filtro/orden/paginación en cliente (solo modo preview/demo). */
function demoSlice(
  all: BusinessReview[],
  opts: { onlyPhotos: boolean; sort: ReviewsSort; offset: number; limit: number },
): { items: BusinessReview[]; total: number } {
  let list = opts.onlyPhotos ? all.filter((r) => r.photos.length > 0) : [...all]
  const ts = (r: BusinessReview) => (r.review_timestamp ? new Date(r.review_timestamp).getTime() : 0)
  switch (opts.sort) {
    case 'oldest': list.sort((a, b) => ts(a) - ts(b)); break
    case 'highest_rating': list.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0)); break
    case 'lowest_rating': list.sort((a, b) => (a.rating ?? 0) - (b.rating ?? 0)); break
    default: list.sort((a, b) => ts(b) - ts(a))
  }
  const total = list.length
  return { items: list.slice(opts.offset, opts.offset + opts.limit), total }
}

export default function ReviewsModal({
  lead,
  open,
  onClose,
  onCompleted,
}: {
  lead: InboxItem | null
  open: boolean
  /** Cerrar el modal. */
  onClose: () => void
  /** Se llama cuando el job termina OK (el drawer re-lee el ángulo de venta). */
  onCompleted?: () => void
}) {
  const placeId = lead?.place_id ?? null

  // Defaults de captura (solo preview): permiten fijar vista tabla / filtro de
  // fotos vía deep-link para el bucle de fidelidad.
  const previewHas = (k: string) =>
    IS_PREVIEW && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has(k)
  const photosDefault = previewHas('rvphotos')

  const [view, setView] = useState<'grid' | 'table'>(previewHas('rvtable') ? 'table' : 'grid')
  const [onlyPhotos, setOnlyPhotos] = useState(photosDefault)
  const [sort, setSort] = useState<ReviewsSort>('newest')
  const [offset, setOffset] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [copied, setCopied] = useState(false)

  const [job, setJob] = useState<ReviewJob | null>(null)
  const [items, setItems] = useState<BusinessReview[]>([])
  const [total, setTotal] = useState(0)
  const [phase, setPhase] = useState<'idle' | 'starting' | 'polling' | 'ready' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<string | null>(null)

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const notifiedRef = useRef(false)

  function clearPoll() {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null }
  }

  // Una lectura del tablero (GET avanza el job en el backend).
  const fetchPage = useCallback(
    async (signal?: AbortSignal): Promise<ReviewJob | null> => {
      if (!placeId) return null
      if (IS_PREVIEW) {
        const { items: it, total: tt } = demoSlice(DEMO_REVIEWS, { onlyPhotos, sort, offset, limit: PAGE_SIZE })
        setItems(it); setTotal(tt); setJob(DEMO_REVIEW_JOB)
        return DEMO_REVIEW_JOB
      }
      const resp = await getReviews(placeId, { only_with_photos: onlyPhotos, sort, offset, limit: PAGE_SIZE }, signal)
      setItems(resp.items); setTotal(resp.total); setJob(resp.job)
      return resp.job
    },
    [placeId, onlyPhotos, sort, offset],
  )

  // Arranque al abrir: GET; si no hay job, dispara POST; luego polling.
  useEffect(() => {
    if (!open || !placeId) return
    notifiedRef.current = false
    let cancelled = false
    const ctrl = new AbortController()

    async function boot() {
      try {
        setErrorMsg(null)
        setPhase('starting')
        if (IS_PREVIEW) {
          await fetchPage()
          if (!cancelled) { setPhase('ready'); onCompleted?.() }
          return
        }
        let current = await fetchPage(ctrl.signal)
        if (cancelled) return
        if (!current) {
          // No hay job aún → dispararlo.
          current = await requestReviews(placeId!, {}, ctrl.signal)
          if (cancelled) return
          setJob(current)
        }
        if (current.status === 'completed') {
          setPhase('ready')
          if (!notifiedRef.current) { notifiedRef.current = true; onCompleted?.() }
        } else if (current.status === 'failed') {
          setPhase('error')
          setErrorMsg(current.error_message ?? 'No se pudieron obtener las reseñas.')
        } else {
          setPhase('polling')
          schedulePoll()
        }
      } catch (e) {
        if (!cancelled) {
          setPhase('error')
          setErrorMsg(e instanceof Error ? e.message : 'No se pudieron obtener las reseñas.')
        }
      }
    }

    function schedulePoll() {
      clearPoll()
      pollRef.current = setTimeout(async () => {
        if (cancelled) return
        try {
          const j = await fetchPage()
          if (cancelled || !j) return
          if (j.status === 'completed') {
            setPhase('ready')
            if (!notifiedRef.current) { notifiedRef.current = true; onCompleted?.() }
          } else if (j.status === 'failed') {
            setPhase('error')
            setErrorMsg(j.error_message ?? 'No se pudieron obtener las reseñas.')
          } else {
            schedulePoll()
          }
        } catch {
          schedulePoll() // reintenta el polling ante un fallo transitorio de red
        }
      }, POLL_MS)
    }

    boot()
    return () => { cancelled = true; ctrl.abort(); clearPoll() }
    // Rearranca solo al (re)abrir o cambiar de lead; filtros se gestionan aparte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placeId])

  // Cambios de filtro/orden/página cuando ya hay reseñas listas → re-GET sin re-disparar.
  useEffect(() => {
    if (!open || !placeId || phase !== 'ready') return
    let cancelled = false
    const ctrl = new AbortController()
    fetchPage(ctrl.signal).catch(() => {})
    return () => { cancelled = true; ctrl.abort(); void cancelled }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyPhotos, sort, offset])

  // Reset al cerrar / cambiar de lead. `onlyPhotos` vuelve a su default (en
  // preview, el del deep-link; en producción, false) — NO a un false fijo, que
  // pisaría el filtro pre-activado de las capturas.
  useEffect(() => {
    if (open) return
    setOffset(0); setOnlyPhotos(photosDefault); setSort('newest')
    setItems([]); setTotal(0); setJob(null); setPhase('idle'); setErrorMsg(null); setLightbox(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placeId])

  // Escape cierra (lightbox primero, luego el modal).
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      if (lightbox) setLightbox(null)
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, lightbox])

  function retry() {
    if (!placeId || IS_PREVIEW) return
    setPhase('starting'); setErrorMsg(null)
    requestReviews(placeId, {})
      .then((j) => {
        setJob(j)
        if (j.status === 'failed') { setPhase('error'); setErrorMsg(j.error_message) }
        else { setPhase('polling'); setOffset(0) }
      })
      .catch((e) => { setPhase('error'); setErrorMsg(e instanceof Error ? e.message : 'Error') })
  }

  // Recoge TODAS las reseñas con el filtro/orden actuales (pagina hasta cubrir
  // el total: el export NO se limita a la página visible).
  async function collectAll(): Promise<BusinessReview[]> {
    if (!placeId) return []
    if (IS_PREVIEW) {
      return demoSlice(DEMO_REVIEWS, { onlyPhotos, sort, offset: 0, limit: DEMO_REVIEWS.length }).items
    }
    const out: BusinessReview[] = []
    const STEP = 200 // límite máx del backend por página
    let off = 0
    for (;;) {
      const resp = await getReviews(placeId, { only_with_photos: onlyPhotos, sort, offset: off, limit: STEP })
      out.push(...resp.items)
      if (resp.items.length === 0 || out.length >= resp.total) break
      off += STEP
      if (off > 5000) break // backstop defensivo
    }
    return out
  }

  async function exportCsv() {
    if (exporting) return
    setExporting(true)
    try {
      const all = await collectAll()
      // BOM para que Excel respete los acentos del español.
      const blob = new Blob(['﻿' + reviewsToCsv(all)], { type: 'text/csv;charset=utf-8' })
      triggerDownload(blob, `resenas-${slugify(lead?.title ?? '')}.csv`)
    } catch {
      /* el botón vuelve a estar disponible; el usuario reintenta */
    } finally {
      setExporting(false)
    }
  }

  async function copyAll() {
    try {
      const all = await collectAll()
      await navigator.clipboard.writeText(reviewsToCsv(all))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      /* portapapeles no disponible: no hacemos nada ruidoso */
    }
  }

  if (!open) return null

  const loading = phase === 'starting' || phase === 'polling'
  const stored = job?.reviews_stored ?? items.length
  const totalKnown = job?.reviews_count_total ?? null
  // `total` (GET) respeta el filtro activo → es el nº MOSTRADO. El UNIVERSO de
  // reseñas traídas es `reviews_stored` del job (no cambia con el filtro). Sin
  // filtro ambos coinciden; con "solo con fotos" difieren (50 vs 6).
  const filtered = total
  const universe = onlyPhotos ? (job?.reviews_stored ?? job?.reviews_count_total ?? total) : total
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const pageIdx = Math.floor(offset / PAGE_SIZE)
  const ratingAvg = lead?.rating_value
  const ratingVotes = lead?.rating_votes_count

  return (
    <div className="reviews-modal-wrap open" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="reviews-modal cabina-form" onClick={(e) => e.stopPropagation()}>
        {/* Cabecera */}
        <div className="rv-head">
          <div className="rv-head-main">
            <span className="rv-head-ic">
              <svg viewBox="0 0 24 24" {...stroke}><path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19.6l1-5.8L3.5 9.7l5.9-.9z" /></svg>
            </span>
            <div style={{ minWidth: 0 }}>
              <h2 className="rv-title">Reseñas · {lead?.title ?? '—'}</h2>
              <div className="rv-sub">
                {ratingAvg != null && (
                  <span className="rv-avg"><b>{ratingAvg.toString().replace('.', ',')}</b>★ {ratingVotes ? `· ${ratingVotes} en Google` : ''}</span>
                )}
                {phase === 'ready' && (
                  // El conteo debe reflejar el TOTAL traído (universe), NO `items`
                  // tras filtrar. Con un filtro activo se indica aparte cuántas
                  // se muestran, para que cuadre con el "X analizadas" del ángulo.
                  onlyPhotos
                    ? <span>{universe} reseña{universe === 1 ? '' : 's'} · mostrando {filtered} con foto{filtered === 1 ? '' : 's'}</span>
                    : <span>{universe} reseña{universe === 1 ? '' : 's'} cargada{universe === 1 ? '' : 's'}</span>
                )}
              </div>
            </div>
          </div>
          <button className="rv-close" onClick={onClose} aria-label="Cerrar">
            <svg viewBox="0 0 24 24" {...stroke}><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {/* Barra de herramientas (oculta mientras carga la primera vez) */}
        {phase === 'ready' && (
          <div className="rv-toolbar">
            <div className="rv-seg">
              <button className={`rv-segbtn${view === 'grid' ? ' on' : ''}`} onClick={() => setView('grid')}>
                <svg viewBox="0 0 24 24" {...stroke}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
                Cuadrícula
              </button>
              <button className={`rv-segbtn${view === 'table' ? ' on' : ''}`} onClick={() => setView('table')}>
                <svg viewBox="0 0 24 24" {...stroke}><path d="M3 5h18M3 12h18M3 19h18" /></svg>
                Tabla
              </button>
            </div>
            <label className={`rv-photos${onlyPhotos ? ' on' : ''}`}>
              <input type="checkbox" checked={onlyPhotos} onChange={(e) => { setOnlyPhotos(e.target.checked); setOffset(0) }} />
              <svg viewBox="0 0 24 24" {...stroke}><rect x="3" y="6" width="18" height="14" rx="2" /><circle cx="12" cy="13" r="3" /><path d="M8 6l1.5-2h5L16 6" /></svg>
              Solo con fotos
            </label>
            <div className="rv-tools-right">
              <div className="rv-sortwrap">
                <span className="rv-sortlab">Ordenar</span>
                <select className="rv-sort" value={sort} onChange={(e) => { setSort(e.target.value as ReviewsSort); setOffset(0) }}>
                  {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {/* Export: recoge TODAS las reseñas con el filtro/orden actuales. */}
              <button
                type="button"
                className="rv-exp"
                onClick={exportCsv}
                disabled={exporting || total === 0}
                title="Exporta todas las reseñas con el filtro y orden actuales"
              >
                <svg viewBox="0 0 24 24" {...stroke}><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" /></svg>
                {exporting ? 'Exportando…' : 'Exportar CSV'}
              </button>
              <button
                type="button"
                className={`rv-exp ghost${copied ? ' ok' : ''}`}
                onClick={copyAll}
                disabled={total === 0}
                title="Copiar las reseñas al portapapeles"
              >
                {copied
                  ? <><svg viewBox="0 0 24 24" {...stroke}><path d="M5 12l5 5 9-10" /></svg>Copiado</>
                  : <><svg viewBox="0 0 24 24" {...stroke}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" /></svg>Copiar</>}
              </button>
            </div>
          </div>
        )}

        {/* Cuerpo */}
        <div className="rv-body">
          {loading && (
            <div className="rv-state">
              <span className="rv-spinner" aria-hidden />
              <div className="rv-state-t">Trayendo reseñas de Google…</div>
              <div className="rv-state-s">
                {phase === 'starting' && 'Lanzando la extracción…'}
                {phase === 'polling' && (totalKnown != null
                  ? `${stored} de ${totalKnown} reseñas procesadas`
                  : `${stored} reseñas procesadas…`)}
              </div>
              {items.length > 0 && <div className="rv-state-s2">Ya puedes ver las primeras mientras llega el resto.</div>}
            </div>
          )}

          {phase === 'error' && (
            <div className="rv-state">
              <span className="rv-state-ic err">
                <svg viewBox="0 0 24 24" {...stroke}><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
              </span>
              <div className="rv-state-t">No se pudieron cargar las reseñas</div>
              <div className="rv-state-s">{errorMsg ?? 'Reinténtalo en unos minutos.'}</div>
              {!IS_PREVIEW && <button className="btn btn-accent btn-sm" onClick={retry}>Reintentar</button>}
            </div>
          )}

          {phase === 'ready' && items.length === 0 && (
            <div className="rv-state">
              <span className="rv-state-ic">
                <svg viewBox="0 0 24 24" {...stroke}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
              </span>
              <div className="rv-state-t">{onlyPhotos ? 'Ninguna reseña con fotos' : 'Sin reseñas'}</div>
              <div className="rv-state-s">
                {onlyPhotos ? 'Quita el filtro de fotos para ver todas las reseñas.' : 'Este negocio aún no tiene reseñas que mostrar.'}
              </div>
            </div>
          )}

          {phase === 'ready' && items.length > 0 && view === 'grid' && (
            <div className="rv-grid">
              {items.map((r) => <ReviewCard key={r.review_id} review={r} onOpenPhoto={setLightbox} />)}
            </div>
          )}

          {phase === 'ready' && items.length > 0 && view === 'table' && (
            <div className="rv-tablewrap">
              <table className="rv-table">
                <thead>
                  <tr>
                    <th>Autor</th><th>Nota</th><th>Fecha</th><th>Reseña</th><th>Fotos</th><th>Respuesta</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.review_id}>
                      <td>
                        <div className="rv-td-author">
                          <Avatar review={r} />
                          <div style={{ minWidth: 0 }}>
                            <div className="rv-td-name">{r.author_name ?? 'Anónimo'}{r.local_guide ? <span className="rv-lg-mini" title="Local Guide">LG</span> : null}</div>
                          </div>
                        </div>
                      </td>
                      <td><Stars rating={r.rating} /></td>
                      <td className="rv-td-date">{reviewDate(r)}</td>
                      <td className="rv-td-text">{r.text || <span className="rv-muted">— sin texto —</span>}</td>
                      <td>
                        {r.photos.length > 0 ? (
                          <div className="rv-td-photos">
                            {r.photos.slice(0, 3).map((p, i) => <Thumb key={i} src={p} onOpen={setLightbox} />)}
                            {r.photos.length > 3 && <span className="rv-more">+{r.photos.length - 3}</span>}
                          </div>
                        ) : <span className="rv-muted">—</span>}
                      </td>
                      <td className="rv-td-owner">{r.owner_answer ? <span className="rv-owner-yes">Sí</span> : <span className="rv-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginación */}
        {phase === 'ready' && total > PAGE_SIZE && (
          <div className="rv-foot">
            <span className="rv-foot-info">
              {offset + 1}–{Math.min(offset + items.length, total)} de {total}
            </span>
            <div className="rv-pager">
              <button className="rv-pg" disabled={pageIdx <= 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                <svg viewBox="0 0 24 24" {...stroke}><path d="M15 6l-6 6 6 6" /></svg>Anterior
              </button>
              <span className="rv-pg-num">Página {pageIdx + 1} / {pageCount}</span>
              <button className="rv-pg" disabled={pageIdx >= pageCount - 1} onClick={() => setOffset(offset + PAGE_SIZE)}>
                Siguiente<svg viewBox="0 0 24 24" {...stroke}><path d="M9 6l6 6-6 6" /></svg>
              </button>
            </div>
          </div>
        )}

        {/* Lightbox de foto */}
        {lightbox && (
          <div className="rv-lightbox" onClick={() => setLightbox(null)}>
            <img src={lightbox} alt="" onClick={(e) => e.stopPropagation()} />
            <button className="rv-lb-close" onClick={() => setLightbox(null)} aria-label="Cerrar foto">
              <svg viewBox="0 0 24 24" {...stroke}><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/** Tarjeta de una reseña (vista cuadrícula). */
function ReviewCard({ review, onOpenPhoto }: { review: BusinessReview; onOpenPhoto: (src: string) => void }) {
  const negative = review.rating != null && review.rating <= 3
  return (
    <article className={`rv-card${negative ? ' neg' : ''}`}>
      <div className="rv-card-head">
        <Avatar review={review} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="rv-card-name">
            {review.author_name ?? 'Anónimo'}
            {review.local_guide && (
              <span className="rv-lg" title="Local Guide">
                <svg viewBox="0 0 24 24" {...stroke}><path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19.6l1-5.8L3.5 9.7l5.9-.9z" /></svg>Local Guide
              </span>
            )}
          </div>
          <div className="rv-card-meta">
            <Stars rating={review.rating} />
            <span className="rv-card-date">{reviewDate(review)}</span>
          </div>
        </div>
      </div>

      {review.text && <p className="rv-card-text">{review.text}</p>}

      {review.photos.length > 0 && (
        <div className="rv-card-photos">
          {review.photos.map((p, i) => <Thumb key={i} src={p} onOpen={onOpenPhoto} />)}
        </div>
      )}

      {review.owner_answer && (
        <div className="rv-card-owner">
          <div className="rv-owner-h">
            <svg viewBox="0 0 24 24" {...stroke}><path d="M9 17l-5-5 5-5M4 12h11a4 4 0 014 4v2" /></svg>
            Respuesta del propietario
          </div>
          <p className="rv-owner-txt">{review.owner_answer}</p>
        </div>
      )}
    </article>
  )
}
